import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { AlertTriangle } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  useUpdateBulkOrderStatusMutation,
  useIssueBulkOrderMutation,
} from "../../../redux/services/orderService";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import "./index.css";
import {
  formatFirestoreTimestamp,
  dateTFormat,
} from "../../../utility/Utility";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import ButtonComponent from "../../../components/ButtonComponent";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPrint, faRedo } from "@fortawesome/free-solid-svg-icons";
import { Order } from "../OrderList";
import { Button } from "react-bootstrap";
import DeliveryPrintSetupModal from "./DeliveryPrintModel";
import {
  setDeliveryOrderDate,
  setDeliveryOrderEndDate,
  setDeliveryOrderCreatedBy,
  setDeliveryOrderStatus,
  setDeliveryCartName,
  clearDeliveryCartName,
  setDeliveryRowsPerPage,
  setDeliveryStatus,
  setDeliveryAgentSourceFilter,
  setDeliveryCartFilterId,
  setOrganizationFilterId,
} from "../../../redux/deliveryOrderSlice";
import { useFirebaseDeliveryOrders } from "../../../hooks/useFirebaseOrders";

const BULK_ACTIONS = [
  { value: "changeStatus", label: "Change Status" },
];

// Updated status flow - Added Pending and removed AcceptedByCart
const STATUS_FLOW = [
  "Pending",
  "ApprovedByRestaurant",
  "Preparing",
  "ReadyAtKitchen",
  "IssuedByKitchen",
  "ArrivedAtCart",
  "PackingComplete",
  "Completed",
  "Cancelled",
];

const ORDER_CREATED_BY_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Customer", label: "Customer" },
  { value: "CartPerson", label: "Cart Person" },
  { value: "Restaurant", label: "Restaurant" }
];

// Updated order status options - include old statuses for display but not for selection
const ORDER_STATUS_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Pending", label: "Pending" },
  { value: "AcceptedByCart", label: "Accepted By Cart" },
  { value: "ApprovedByRestaurant", label: "Approved By Restaurant" },
  { value: "Preparing", label: "Preparing" },
  { value: "ReadyAtKitchen", label: "Ready At Kitchen" },
  { value: "IssuedByKitchen", label: "Issued By Kitchen" },
  { value: "ArrivedAtCart", label: "Arrived At Cart" },
  { value: "PackingComplete", label: "Packing Complete" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Tentative", label: "Tentative" },
];

const DELIVERY_STATUS_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Pending", label: "Pending" },
  { value: "Packing", label: "Packing" },
  { value: "PackingComplete", label: "Packing Complete" },
  { value: "Dispatched", label: "Dispatched" },
  { value: "OutForDelivery", label: "Out For Delivery" },
  { value: "Delivered", label: "Delivered" },
];

const ALL_AGENT_SOURCE_OPTION = { value: "all", label: "All", type: "all" };

// const COMPLETED_ORDER_STATUS = "Completed";
// const ISSUED_BY_KITCHEN_ORDER_STATUS = "IssuedByKitchen";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PACKING_COMPLETE_STATUS = "PackingComplete";
// const TENTATIVE_ORDER_STATUS = "Tentative";
const ORDER_CREATED_BY_CART_PERSON = "CartPerson";
const ORDER_CREATED_BY_CUSTOMER = "Customer";

// Statuses that should NOT have checkboxes enabled - Removed Pending from here
const RESTRICTED_STATUSES = [
  "AcceptedByCart",
  "Completed",
  "Cancelled",
  "Tentative",
];

// Statuses that are eligible for bulk actions - Added Pending
const ELIGIBLE_FOR_BULK_ACTIONS = [
  "Pending",
  "ApprovedByRestaurant",
  "Preparing",
  "ReadyAtKitchen",
  "IssuedByKitchen",
  "ArrivedAtCart",
  "PackingComplete",
];

type CartFilterOption = {
  value: string;
  label: string;
  cartId: string;
  displayName: string;
};

const DeliveryOrderManagement: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  // Get state from Redux
  const deliveryOrderCreatedBy = useSelector(
    (state: any) => state.deliveryOrder.orderCreatedBy,
  );
  const deliveryOrderStatus = useSelector(
    (state: any) => state.deliveryOrder.orderStatus,
  );
  const deliveryStatus = useSelector(
    (state: any) => state.deliveryOrder.deliveryStatus,
  );
  const deliveryStartDate = useSelector(
    (state: any) => state.deliveryOrder.orderDate,
  );
  const deliveryEndDate = useSelector(
    (state: any) => state.deliveryOrder.orderEndDate,
  );
  const deliveryRowsPerPage = useSelector(
    (state: any) => state.deliveryOrder.rowsPerPage,
  );
  const deliveryCartName = useSelector(
    (state: any) => state.deliveryOrder.cartName,
  );
  const deliveryShowOnlyUnassigned = useSelector(
    (state: any) => state.deliveryOrder.showOnlyUnassigned,
  );
  const deliveryAgentSourceFilter = useSelector(
    (state: any) => state.deliveryOrder.agentSourceFilter,
  );
  const persistedDeliveryCartId = useSelector(
    (state: any) => state.deliveryOrder.deliveryCartId,
  );
  const persistedOrganizationId = useSelector(
    (state: any) => state.deliveryOrder.organizationId,
  );

  // Convert date strings to Date objects for Firebase hook
  const startDateObj = deliveryStartDate
    ? new Date(deliveryStartDate.split("T")[0] + "T00:00:00")
    : new Date(new Date().toISOString().split("T")[0] + "T00:00:00");
  const endDateObj = deliveryEndDate
    ? new Date(deliveryEndDate.split("T")[0] + "T23:59:59")
    : new Date(new Date().toISOString().split("T")[0] + "T23:59:59");

  // Use Firebase real-time hook for orders
  const { data: ordersData, loading: ordersLoading } =
    useFirebaseDeliveryOrders(RestID, startDateObj, endDateObj);

  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrderCreatedBy, setSelectedOrderCreatedBy] = useState({
    value: deliveryOrderCreatedBy,
    label: deliveryOrderCreatedBy,
  });
  const [selectedOrderStatus, setSelectedOrderStatus] = useState({
    value: deliveryOrderStatus,
    label: deliveryOrderStatus,
  });
  const [selectedDeliveryStatus, setSelectedDeliveryStatus] = useState({
    value: deliveryStatus,
    label:
      DELIVERY_STATUS_OPTIONS.find((option) => option.value === deliveryStatus)
        ?.label || deliveryStatus,
  });
  const [selectedSourceCart, setSelectedSourceCart] = useState<any>(null);
  const [selectedDeliveryCart, setSelectedDeliveryCart] = useState<any>(null);
  const [bulkAction, setBulkAction] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [nextStatusOption, setNextStatusOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentStatusForSelected, setCurrentStatusForSelected] =
    useState<string>("");
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [sourceSearchOptions, setSourceSearchOptions] = useState<CartFilterOption[]>([]);
  const [allSourceCartOptions, setAllSourceCartOptions] = useState<CartFilterOption[]>([]);
  const [deliverySearchOptions, setDeliverySearchOptions] = useState<CartFilterOption[]>([]);
  const [allDeliveryCartOptions, setAllDeliveryCartOptions] = useState<CartFilterOption[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<any>(
    deliveryAgentSourceFilter || ALL_AGENT_SOURCE_OPTION,
  );
  const [agentOptions, setAgentOptions] = useState<any>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<any>(null);
  const [organizationOptions, setOrganizationOptions] = useState<any[]>([]);

  const [updateBulkOrderStatus, { isLoading: isUpdatingBulkStatus }] =
    useUpdateBulkOrderStatusMutation();
  const [issueBulkOrder, { isLoading: isIssuingBulkOrder }] =
    useIssueBulkOrderMutation();

  const hasCartDeliveryPerson = (order: Order) => {
    return (
      order?.cart?.cartPersonId !== undefined &&
      order?.cart?.cartPersonId !== null &&
      order?.cart?.cartPersonId !== ""
    );
  };

  const handleOrganizationChange = (selectedOption: any) => {
    const nextOption = selectedOption || { value: "all", label: "All" };
    setSelectedOrganization(nextOption);
    dispatch(setOrganizationFilterId(nextOption.value === "all" ? "" : nextOption.value));
  };

  const isOrderEligibleForBulkActions = (order: Order): boolean => {
    return ELIGIBLE_FOR_BULK_ACTIONS.includes(order.orderStatus);
  };

  const shouldShowCheckbox = (order: Order) => {
    if (isParentOrder(order)) return false;
    if (RESTRICTED_STATUSES.includes(order.orderStatus)) return false;
    if (!isOrderEligibleForBulkActions(order)) return false;

    if (order.orderStatus === "ReadyAtKitchen") {
      return hasCartDeliveryPerson(order);
    }

    // PackingComplete requires a delivery cart to be assigned
    if (order.orderStatus === "PackingComplete") {
      return !!(order.deliveryCart && order.deliveryCart.cartId);
    }

    // All other eligible statuses (Pending, ApprovedByRestaurant, Preparing, IssuedByKitchen, ArrivedAtCart)
    if (ELIGIBLE_FOR_BULK_ACTIONS.includes(order.orderStatus)) {
      return true;
    }

    return false;
  };

  const isOrderSelectable = (order: Order) => {
    // Don't allow selection of parent orders
    if (isParentOrder(order)) {
      return false;
    }

    // Don't allow selection if checkbox shouldn't be shown
    if (!shouldShowCheckbox(order)) {
      return false;
    }

    // Check if order is eligible for bulk actions
    if (!isOrderEligibleForBulkActions(order)) {
      return false;
    }

    // If no orders are selected yet, allow selection
    if (selectedOrders.length === 0) {
      return true;
    }

    // If orders are already selected, only allow selection of orders with same status
    const firstOrder = orders.find((o) => o.id === selectedOrders[0]);
    return firstOrder && order.orderStatus === firstOrder.orderStatus;
  };

  const getOrderType = (order: Order): string => {
    // Child order: has parentOrderId AND isSplitOrder is true
    if (order.parentOrderId && order.isSplitOrder === true) {
      return "Child";
    }
    // Parent order: has no parentOrderId AND isSplitOrder is true
    else if (!order.parentOrderId && order.isSplitOrder === true) {
      return "Parent";
    }
    // Normal order: no parentOrderId and isSplitOrder is false
    return "Normal";
  };

  const isParentOrder = (order: Order): boolean => {
    return getOrderType(order) === "Parent";
  };

  const getTypeBadgeColor = (type: string): string => {
    switch (type) {
      case "Parent":
        return "primary";
      case "Child":
        return "info";
      case "Normal":
      default:
        return "secondary";
    }
  };

  const getSelectableOrders = () => {
    let selectableOrders = filteredOrders;

    selectableOrders = selectableOrders.filter((order) =>
      shouldShowCheckbox(order),
    );

    if (currentStatusForSelected) {
      selectableOrders = selectableOrders.filter(
        (order) => order.orderStatus === currentStatusForSelected,
      );
    }

    return selectableOrders;
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedStartDate = new Date(e.target.value);
    const currentEndDate = new Date(deliveryEndDate.split("T")[0]);

    if (selectedStartDate > currentEndDate) {
      toast.error("Start date cannot be after end date");
      return;
    }

    const formattedDate = dateTFormat(selectedStartDate);
    dispatch(setDeliveryOrderDate(formattedDate));
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedEndDate = new Date(e.target.value);
    const currentStartDate = new Date(deliveryStartDate.split("T")[0]);

    if (selectedEndDate < currentStartDate) {
      toast.error("End date cannot be before start date");
      return;
    }

    const formattedDate = dateTFormat(selectedEndDate);
    dispatch(setDeliveryOrderEndDate(formattedDate));
  };

  useEffect(() => {
    if (ordersData && ordersData.orders) {
      const transformedOrders: Order[] = ordersData.orders.map(
        (order: any, index: number) => ({
          id: order.id || `order-${index}`,
          orderReference: order.orderReference || `REF-${index}`,
          orderCreatedBy: order.orderCreatedBy,
          displayName: order.cart?.displayName || "",
          createdDate: formatFirestoreTimestamp(order.createdAt),
          orderStatus: order.orderStatus,
          deliveryStatus: order.deliveryStatus,
          selected: false,
          address: order.address || "No address provided",
          cart: order.cart,
          customer: order.customer,
          organization: order.organization,
          isDirectRestaurantOrder: order.isDirectRestaurantOrder || false,
          orderItems: order.orderItems || [],
          paymentMethod: order.paymentMethod || "",
          totalPrice: order.totalPrice || 0,
          createdAt: order.createdAt,
          deliverAt: order.deliverAt,
          walletPaymentAmount: order.walletPaymentAmount,
          deliveryCart: order.deliveryCart,
          deliveryDetails: order.deliveryDetails,
          paymentCompleted: order.paymentCompleted,
          isScheduledOrder: order.isScheduledOrder,
          paymentSummary: order.paymentSummary,
          deliveryTimeSlot: order.deliveryTimeSlot || null,
          pickupTimeSlot: order.pickupTimeSlot || null,
          restaurantId: order.restaurantId,
          phoneNumber: order.customer?.phoneNumber || "",
          orderSource: order.orderSource,
          orderTakenBy: order.orderTakenBy,
          isSplitOrder: order.isSplitOrder || false,
          parentOrderId: order.parentOrderId || null,
          isDeliveryOrder: order.isDeliveryOrder ?? true,
          orderNote: order.orderNote || "",
          packingNote: order.packingNote || "",
          paymentNote: order.paymentNote || "",
          promoCode: order.promoCode || null,
        }),
      );

      const sortedOrders = [...transformedOrders].sort((a, b) =>
        (b.orderReference || "").localeCompare(a.orderReference || "", undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      );

      setOrders(sortedOrders);
    }
  }, [ordersData]);

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (!query) {
      setFilteredOrders(orders);
      return;
    }

    const filteredData = orders.filter((order) => {
      const searchableFields = [
        order.orderReference?.toLowerCase() || "",
        order.phoneNumber?.toLowerCase() || "",
        order.customer?.displayName?.toLowerCase() || "",
        order.organization?.displayName?.toLowerCase() || "",
        order.cart?.displayName?.toLowerCase() || "",
      ];

      return searchableFields.some((field) => field.includes(query));
    });

    setFilteredOrders(filteredData);
  };

  useEffect(() => {
    let filtered = [...orders];

    if (searchQuery) {
      filtered = filtered.filter((order) => {
        const searchableFields = [
          order.orderReference?.toLowerCase() || "",
          order.phoneNumber?.toLowerCase() || "",
          order.customer?.displayName?.toLowerCase() || "",
          order.organization?.displayName?.toLowerCase() || "",
          order.cart?.displayName?.toLowerCase() || "",
        ];
        return searchableFields.some((field) =>
          field.includes(searchQuery.toLowerCase()),
        );
      });
    }

    if (selectedOrderCreatedBy.value !== "All") {
      filtered = filtered.filter(
        (order) => order.orderCreatedBy === selectedOrderCreatedBy.value,
      );
    }

    if (selectedOrderStatus.value !== "All") {
      filtered = filtered.filter(
        (order) => order.orderStatus === selectedOrderStatus.value,
      );
    }

    if (selectedDeliveryStatus.value !== "All") {
      filtered = filtered.filter(
        (order) => order.deliveryStatus === selectedDeliveryStatus.value,
      );
    }

    if (selectedSourceCart && selectedSourceCart.value !== "all") {
      const selectedSourceCartId = selectedSourceCart?.cartId;
      const selectedSourceCartName = (
        selectedSourceCart?.displayName || selectedSourceCart?.label || ""
      ).toLowerCase();

      filtered = filtered.filter((order) => {
        const orderSourceCartId = order.cart?.cartId;
        if (selectedSourceCartId) {
          return orderSourceCartId === selectedSourceCartId;
        }

        const orderSourceCartName = (order.cart?.displayName || "").toLowerCase();
        return orderSourceCartName === selectedSourceCartName;
      });
    }

    if (selectedDeliveryCart && selectedDeliveryCart.value !== "all") {
      const selectedDeliveryCartId = selectedDeliveryCart?.cartId;
      const selectedDeliveryCartName = (
        selectedDeliveryCart?.displayName || selectedDeliveryCart?.label || ""
      ).toLowerCase();

      filtered = filtered.filter((order) => {
        const orderDeliveryCartId = order.deliveryCart?.cartId;
        if (selectedDeliveryCartId) {
          return orderDeliveryCartId === selectedDeliveryCartId;
        }

        const orderDeliveryCartName =
          (order.deliveryCart?.displayName || "").toLowerCase();
        return orderDeliveryCartName === selectedDeliveryCartName;
      });
    }

    if (selectedOrganization && selectedOrganization.value !== "all") {
      filtered = filtered.filter(
        (order) =>
          order.organization &&
          (order.organization.organizationId === selectedOrganization.value ||
            order.organization.id === selectedOrganization.value),
      );
    }

    if (deliveryShowOnlyUnassigned) {
      filtered = filtered.filter((order) => !order.displayName);
    }

    // Apply agent/source filter
    if (selectedAgent && selectedAgent.value !== "all") {
      filtered = filtered.filter((order: Order) => {
        // Filter by call center agent
        if (selectedAgent.type === "agent") {
          return (
            order.orderSource === "CallCenter" &&
            order.orderTakenBy?.displayName === selectedAgent.label
          );
        } else if (selectedAgent.type === "source") {
          return order.orderSource === selectedAgent.value;
        }
        return true;
      });
    }

    setFilteredOrders(filtered);
  }, [
    orders,
    searchQuery,
    selectedOrderCreatedBy,
    selectedOrderStatus,
    selectedDeliveryStatus,
    selectedSourceCart,
    selectedDeliveryCart,
    selectedOrganization,
    deliveryShowOnlyUnassigned,
    selectedAgent,
  ]);

  useEffect(() => {
    if (orders.length === 0) return;

    const allOption: CartFilterOption = {
      value: "all",
      label: "All",
      cartId: "all",
      displayName: "All",
    };

    const sourceCartMap = new Map<string, CartFilterOption>();
    const deliveryCartMap = new Map<string, CartFilterOption>();

    orders.forEach((order: any) => {
      const sourceCart = order?.cart;
      const sourceDisplayName = sourceCart?.displayName?.trim();

      if (sourceDisplayName) {
        const sourceCartId = sourceCart?.cartId || "";
        const sourceKey = sourceCartId
          ? `id:${sourceCartId}`
          : `name:${sourceDisplayName.toLowerCase()}`;

        if (!sourceCartMap.has(sourceKey)) {
          sourceCartMap.set(sourceKey, {
            value: sourceCartId || `name:${sourceDisplayName.toLowerCase()}`,
            label: sourceDisplayName,
            cartId: sourceCartId,
            displayName: sourceDisplayName,
          });
        }
      }

      const deliveryCart = order?.deliveryCart;
      const deliveryDisplayName = deliveryCart?.displayName?.trim();

      if (deliveryDisplayName) {
        const deliveryCartId = deliveryCart?.cartId || "";
        const deliveryKey = deliveryCartId
          ? `id:${deliveryCartId}`
          : `name:${deliveryDisplayName.toLowerCase()}`;

        if (!deliveryCartMap.has(deliveryKey)) {
          deliveryCartMap.set(deliveryKey, {
            value: deliveryCartId || `name:${deliveryDisplayName.toLowerCase()}`,
            label: deliveryDisplayName,
            cartId: deliveryCartId,
            displayName: deliveryDisplayName,
          });
        }
      }
    });

    const dynamicSourceCartOptions = Array.from(sourceCartMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    const dynamicDeliveryCartOptions = Array.from(deliveryCartMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );

    const sourceOptions = [allOption, ...dynamicSourceCartOptions];
    const deliveryOptions = [allOption, ...dynamicDeliveryCartOptions];

    setAllSourceCartOptions(sourceOptions);
    setSourceSearchOptions(sourceOptions);
    setAllDeliveryCartOptions(deliveryOptions);
    setDeliverySearchOptions(deliveryOptions);

    if (deliveryCartName) {
      const matchingCart = sourceOptions.find(
        (cart) => cart.label.toLowerCase() === deliveryCartName.toLowerCase(),
      );
      if (matchingCart) {
        setSelectedSourceCart(matchingCart);
      } else {
        setSelectedSourceCart(allOption);
        dispatch(setDeliveryCartName("All"));
      }
    } else {
      setSelectedSourceCart(allOption);
      dispatch(setDeliveryCartName("All"));
    }

    const currentSelectedSource = sourceOptions.find(
      (cart) => cart.value === selectedSourceCart?.value,
    );
    if (currentSelectedSource) {
      setSelectedSourceCart(currentSelectedSource);
    }

    const currentSelectedDelivery = deliveryOptions.find(
      (cart) => cart.value === selectedDeliveryCart?.value,
    );
    if (currentSelectedDelivery) {
      setSelectedDeliveryCart(currentSelectedDelivery);
    } else if (persistedDeliveryCartId) {
      const restoredDeliveryCart = deliveryOptions.find(
        (cart) => cart.value === persistedDeliveryCartId,
      );
      setSelectedDeliveryCart(restoredDeliveryCart || allOption);
    } else {
      setSelectedDeliveryCart(allOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, deliveryCartName, dispatch, persistedDeliveryCartId]);

  // Build organization options from all orders
  useEffect(() => {
    if (orders.length > 0) {
      const orgMap = new Map();
      orders.forEach((order) => {
        if (
          order.organization &&
          order.organization.organizationId &&
          order.organization.displayName
        ) {
          orgMap.set(
            order.organization.organizationId,
            order.organization.displayName,
          );
        }
      });
      const orgOptions = Array.from(orgMap.entries()).map(
        ([id, displayName]) => ({
          value: id,
          label: displayName,
        }),
      );
    const allOption = { value: "all", label: "All" };
      const fullOrgOptions = [allOption, ...orgOptions];
      setOrganizationOptions(fullOrgOptions);
      if (!selectedOrganization) {
        const restoredOrg = persistedOrganizationId
          ? fullOrgOptions.find((o) => o.value === persistedOrganizationId)
          : null;
        setSelectedOrganization(restoredOrg || allOption);
      }
    }
  }, [orders, selectedOrganization, persistedOrganizationId]);

  // Build agent options from all orders
  useEffect(() => {
    if (orders.length > 0) {
      const agents = new Set<string>();
      const sources = new Set<string>();

      orders.forEach((order) => {
        // Collect call center agents
        if (
          order.orderSource === "CallCenter" &&
          order.orderTakenBy?.displayName
        ) {
          agents.add(order.orderTakenBy.displayName);
        }
        // Collect order sources - include any valid orderSource value
        if (order.orderSource && order.orderSource !== "CallCenter") {
          sources.add(order.orderSource);
        }
      });

      const agentOpts = Array.from(agents)
        .sort()
        .map((agent) => ({
          value: agent,
          label: agent,
          type: "agent",
        }));

      const sourceOpts = Array.from(sources)
        .sort()
        .map((source) => ({
          value: source,
          label:
            source === "CustomerApp"
              ? "Customer App"
              : source === "CustomerWeb"
                ? "Customer Web"
                : source === "WhatsApp"
                  ? "WhatsApp"
                  : source,
          type: "source",
        }));

      const computedAgentOptions = [
        ALL_AGENT_SOURCE_OPTION,
        ...agentOpts,
        ...sourceOpts,
      ];
      setAgentOptions(computedAgentOptions);

      const matchedSelectedAgent = computedAgentOptions.find(
        (option) =>
          option.value === selectedAgent?.value &&
          option.type === selectedAgent?.type,
      );

      if (
        matchedSelectedAgent &&
        (matchedSelectedAgent.value !== selectedAgent?.value ||
          matchedSelectedAgent.type !== selectedAgent?.type)
      ) {
        setSelectedAgent(matchedSelectedAgent);
      }
    }
  }, [orders, selectedAgent]);

  const handleOrderSelection = (orderId: string) => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;

    // Prevent selection of parent orders
    if (isParentOrder(order)) {
      toast.info("Parent orders cannot be selected for bulk actions");
      return;
    }

    // Check if order is eligible for bulk actions
    if (!isOrderEligibleForBulkActions(order)) {
      toast.info(`${order.orderStatus} orders cannot be selected for bulk actions`);
      return;
    }

    if (!shouldShowCheckbox(order)) {
      if (
        order.orderStatus === "ReadyAtKitchen" &&
        !hasCartDeliveryPerson(order)
      ) {
        toast.info(
          "This order cannot be issued because the cart is not assigned to a delivery person",
        );
      } else {
        toast.info("This order status is not available for bulk actions");
      }
      return;
    }

    if (!isOrderSelectable(order)) {
      if (selectedOrders.length > 0) {
        const firstOrder = orders.find((o) => o.id === selectedOrders[0]);
        toast.error(
          `You can only select orders with status "${firstOrder?.orderStatus}" for bulk actions`,
        );
      }
      return;
    }

    let newSelectedOrders: string[];
    if (selectedOrders.includes(orderId)) {
      newSelectedOrders = selectedOrders.filter((id) => id !== orderId);
      setCurrentStatusForSelected(
        newSelectedOrders.length > 0 ? currentStatusForSelected : "",
      );
    } else {
      if (selectedOrders.length === 0) {
        setCurrentStatusForSelected(order.orderStatus);
        newSelectedOrders = [orderId];
      } else {
        newSelectedOrders = [...selectedOrders, orderId];
      }
    }

    setSelectedOrders(newSelectedOrders);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const selectableOrders = getSelectableOrders();

      const trulySelectableOrders = selectableOrders.filter((order) =>
        shouldShowCheckbox(order),
      );

      if (trulySelectableOrders.length === 0) {
        toast.info("No selectable orders available");
        return;
      }

      if (selectedOrders.length === 0) {
        toast.info(
          "Please select at least one order first to determine the status for bulk actions",
        );
        return;
      }

      setSelectedOrders(trulySelectableOrders.map((order) => order.id));
    } else {
      setSelectedOrders([]);
      setCurrentStatusForSelected("");
    }
  };

  const getNextStatus = (currentStatus: string, order: Order) => {
    const currentIndex = STATUS_FLOW.indexOf(currentStatus);
    if (currentIndex === -1 || currentIndex >= STATUS_FLOW.length - 1) {
      return null;
    }

    // Special case for Pending -> ApprovedByRestaurant
    if (currentStatus === "Pending") {
      return {
        value: "ApprovedByRestaurant",
        label: "Approved By Restaurant",
      };
    }

    if (currentStatus === "IssuedByKitchen") {
      return {
        value: "ArrivedAtCart",
        label: "Arrived At Cart",
      };
    }

    if (currentStatus === "ArrivedAtCart") {
      return {
        value: "PackingComplete",
        label: "Packing Complete",
      };
    }

    const nextStatus = STATUS_FLOW[currentIndex + 1];
    return {
      value: nextStatus,
      label: nextStatus.replace(/([A-Z])/g, " $1").trim(),
    };
  };

  const handleBulkActionChange = (selectedOption: any) => {
    setBulkAction(selectedOption);

    if (selectedOption?.value === "changeStatus" && selectedOrders.length > 0) {
      const firstOrder = orders.find((o) => o.id === selectedOrders[0]);
      if (firstOrder) {
        const nextStatus = getNextStatus(currentStatusForSelected, firstOrder);
        setNextStatusOption(nextStatus);
        setShowBulkStatusModal(true);
      }
    } else if (selectedOption?.value === "issue") {
      setShowConfirmModal(true);
    }
  };

  const handleBulkActionSubmit = async () => {
    if (!bulkAction || !nextStatusOption || selectedOrders.length === 0) {
      toast.error("Please select an action and status");
      return;
    }

    try {
      await updateBulkOrderStatus({
        orderIds: selectedOrders,
        orderStatus: nextStatusOption.value,
      }).unwrap();

      toast.success(`Updated ${selectedOrders.length} orders to ${nextStatusOption.label}`);
      resetActionState();
      setNextStatusOption(null);
      setShowBulkStatusModal(false);
    } catch (error) {
      console.error("Bulk status update error:", error);
      toast.error("Failed to update orders");
    }
  };

  const handleIssueOrders = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Please select orders to issue");
      return;
    }

    try {
      await issueBulkOrder({
        orderIds: selectedOrders,
      }).unwrap();

      toast.success(`Issued ${selectedOrders.length} orders`);

      resetActionState();

      setShowConfirmModal(false);
    } catch (error: any) {
      console.error("Issue orders error:", error);
      const message = error.data?.message || "Failed to issue orders";
      toast.error(message);
    }
  };

  const handleOrderCreatedByChange = (selectedOption: any) => {
    setSelectedOrderCreatedBy(selectedOption);
    dispatch(setDeliveryOrderCreatedBy(selectedOption.value));
  };

  const handleOrderStatusChange = (selectedOption: any) => {
    setSelectedOrderStatus(selectedOption);
    dispatch(setDeliveryOrderStatus(selectedOption.value));
  };

  const handleDeliveryStatusChange = (selectedOption: any) => {
    const nextOption = selectedOption || DELIVERY_STATUS_OPTIONS[0];
    setSelectedDeliveryStatus(nextOption);
    dispatch(setDeliveryStatus(nextOption.value));
  };

  const handleSourceCartSelect = (selectedOption: any) => {
    setSelectedSourceCart(selectedOption);

    if (!selectedOption) {
      dispatch(clearDeliveryCartName());
    } else {
      dispatch(setDeliveryCartName(selectedOption.label));
    }
  };

  const handleDeliveryCartSelect = (selectedOption: any) => {
    setSelectedDeliveryCart(selectedOption);
    dispatch(setDeliveryCartFilterId(selectedOption?.value || ""));
  };

  const handleSourceCartInputChange = (inputValue: string) => {
    if (!inputValue || inputValue.length === 0) {
      setSourceSearchOptions(allSourceCartOptions);
      return;
    }

    const lowerInput = inputValue.toLowerCase();
    const filteredOptions = allSourceCartOptions.filter(
      (option) =>
        option.value === "all" ||
        option.label.toLowerCase().includes(lowerInput),
    );

    setSourceSearchOptions(filteredOptions);
  };

  const handleDeliveryCartInputChange = (inputValue: string) => {
    if (!inputValue || inputValue.length === 0) {
      setDeliverySearchOptions(allDeliveryCartOptions);
      return;
    }

    const lowerInput = inputValue.toLowerCase();
    const filteredOptions = allDeliveryCartOptions.filter(
      (option) =>
        option.value === "all" ||
        option.label.toLowerCase().includes(lowerInput),
    );

    setDeliverySearchOptions(filteredOptions);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setDeliveryRowsPerPage(newRowsPerPage));
  };

  const handleViewDetails = (order: Order) => {
    navigate("/orders/delivery-order/view", { state: { orderId: order.id } });
  };

  const handleReorder = (order: Order) => {
    navigate("/orders/create-order", {
      state: {
        reorderData: {
          isDeliveryOrder: order.isDeliveryOrder ?? true,
          orderCreatedBy: order.orderCreatedBy,
          customer: order.customer ?? null,
          cart: order.cart ?? null,
          organization: order.organization ? {
            organizationId: order.organization.organizationId || order.organization.id,
            id: order.organization.id || order.organization.organizationId,
            displayName: order.organization.displayName,
            phoneNumber: order.organization.phoneNumber,
          } : null,
          phoneNumber: order.phoneNumber ?? order.customer?.phoneNumber ?? null,
          paymentMethod: order.paymentMethod ?? null,
          orderNote: order.orderNote ?? "",
          packingNote: order.packingNote ?? "",
          paymentNote: order.paymentNote ?? "",
          deliveryDetails: order.deliveryDetails ?? null,
          deliveryTimeSlot: order.deliveryTimeSlot ?? null,
          pickupTimeSlot: order.pickupTimeSlot ?? null,
          paymentSummary: order.paymentSummary ?? null,
          isManualDeliveryFee: order.paymentSummary?.isManualDeliveryFee ?? false,
          manualDeliveryFee: order.paymentSummary?.isManualDeliveryFee
            ? (order.paymentSummary?.deliveryFee ?? 0)
            : null,
          orderItems: (order.orderItems || []).map((item: any) => ({
            id: item.menuItemId,
            menuItemId: item.menuItemId,
            tinyId: item.tinyId,
            skuId: item.skuId,
            displayName: item.displayName,
            price: item.unitPrice,
            calculatedPrice: item.unitPrice,
            quantity: item.quantity,
            portionLabel: item.portionSize || "Regular",
            itemType: item.itemType,
            customItems: item.customItems || [],
          })),
        },
      },
    });
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Pending":              return "#F97316";
      case "AcceptedByCart":       return "#8B5CF6";
      case "ApprovedByRestaurant": return "#FFC107";
      case "Collecting":           return "#06B6D4";
      case "Preparing":            return "#0D6EFD";
      case "ReadyAtKitchen":       return "#212529";
      case "IssuedByKitchen":      return "#6366F1";
      case "ArrivedAtCart":        return "#EC4899";
      case "InAppCart":            return "#14B8A6";
      case "PackingComplete":      return "#84CC16";
      case "Completed":            return "#198754";
      case "Tentative":            return "#94A3B8";
      case "Cancelled":            return "#DC3545";
      default:                     return "#DC3545";
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case "ApprovedByRestaurant":
      case "PackingComplete":
      case "Tentative":
        return "#333";
      default:
        return "#fff";
    }
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case "Pending":              return "Pending";
      case "AcceptedByCart":       return "Accepted By Cart";
      case "ApprovedByRestaurant": return "Approved By Restaurant";
      case "Collecting":           return "Collecting";
      case "Preparing":            return "Preparing";
      case "ReadyAtKitchen":       return "Ready At Kitchen";
      case "IssuedByKitchen":      return "Issued By Kitchen";
      case "ArrivedAtCart":        return "Arrived At Cart";
      case "InAppCart":            return "In App Cart";
      case "PackingComplete":      return "Packing Complete";
      case "Completed":            return "Completed";
      case "Tentative":            return "Tentative";
      case "Cancelled":            return "Cancelled";
      default:                     return "Cancelled";
    }
  };

  const getStatusDisplay = (row: Order) => (
    <div className="d-flex flex-column align-items-left">
      <span
        style={{
          backgroundColor: getStatusBadgeColor(row?.orderStatus),
          color: getStatusTextColor(row?.orderStatus),
          fontSize: 12,
          padding: "5px 16px",
          borderRadius: "4px",
          display: "inline-block",
          fontWeight: 500,
        }}
      >
        {getStatusDisplayText(row?.orderStatus)}
      </span>
    </div>
  );

  const resetActionState = () => {
    setSelectedOrders([]);
    setCurrentStatusForSelected("");
    setBulkAction(null);
  };

  const handleAgentChange = (selectedOption: any) => {
    const nextOption = selectedOption || ALL_AGENT_SOURCE_OPTION;
    setSelectedAgent(nextOption);
    dispatch(setDeliveryAgentSourceFilter(nextOption));
  };

  const columns: TableColumn<Order>[] = [
    {
      name: (
        <input
          type="checkbox"
          checked={
            selectedOrders.length > 0 &&
            getSelectableOrders().length > 0 &&
            getSelectableOrders().every((order) =>
              selectedOrders.includes(order.id),
            )
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={
            getSelectableOrders().length === 0 || selectedOrders.length === 0
          }
          title={
            selectedOrders.length === 0
              ? "Select at least one order first"
              : getSelectableOrders().length === 0
                ? "No selectable orders available"
                : "Select all"
          }
          className="large-checkbox"
        />
      ),
      cell: (row: Order) => {
        if (!shouldShowCheckbox(row)) {
          return null;
        }

        const isSelectable = isOrderSelectable(row);
        const isChecked = selectedOrders.includes(row.id);

        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => handleOrderSelection(row.id)}
            disabled={!isSelectable}
            title={
              !isSelectable
                ? selectedOrders.length > 0
                  ? `Can only select orders with status "${currentStatusForSelected}"`
                  : row.orderStatus === "ReadyAtKitchen" &&
                    !hasCartDeliveryPerson(row)
                    ? "Cart not assigned to delivery person"
                    : "Select order"
                : "Select order"
            }
            className="large-checkbox"
          />
        );
      },
      width: "60px",
    },
    {
      name: <h6 className="fw-bold">Reorder</h6>,
      cell: (row: Order) => (
        <Button onClick={(e) => { e.stopPropagation(); handleReorder(row); }} variant="light">
          <FontAwesomeIcon icon={faRedo} color="#FF9A62" />
        </Button>
      ),
      width: "90px",
      center: true,
    },
    {
      name: <h6 className="fw-bold">Reference No</h6>,
      selector: (row: Order) => row.orderReference,
      sortable: true,
      cell: (row: Order) => (
        <div className="d-flex align-items-center">
          <strong>{row.orderReference}</strong>
        </div>
      ),
      width: "160px",
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      cell: (row: Order) => getStatusDisplay(row),
      sortable: true,
      center: true,
      width: "200px",
    },
    {
      name: <h6 className="fw-bold">Creator</h6>,
      selector: (row: Order) => row.orderCreatedBy,
      sortable: true,
      cell: (row: Order) => (
        <span>
          {row.orderCreatedBy === ORDER_CREATED_BY_CART_PERSON ? (
            <>Cart Person</>
          ) : row.orderCreatedBy === ORDER_CREATED_BY_CUSTOMER ? (
            <>Customer</>
          ) : (
            <>{row.orderTakenBy?.displayName || "Restaurant"}</>
          )}
        </span>
      ),
      width: "150px",
    },
    {
      name: <h6 className="fw-bold">Name</h6>,
      selector: (row: Order) => {
        if (row.orderCreatedBy === "Customer" && row.customer) {
          return row.customer.displayName;
        }
        if (row.orderCreatedBy === "Restaurant") {
          // For restaurant orders, prioritize organization name over customer name
          if (row.organization) {
            return row.organization.displayName;
          } else if (row.customer) {
            return row.customer.displayName;
          }
        }
        if (row.cart?.displayName) {
          return row.cart.displayName;
        }
        return row.orderCreatedBy;
      },
      sortable: true,
      cell: (row: Order) => {
        let creatorName = row.orderCreatedBy;

        if (row.orderCreatedBy === "Customer" && row.customer) {
          creatorName = row.customer.displayName;
        } else if (row.orderCreatedBy === "Restaurant") {
          // For restaurant orders, prioritize organization name over customer name
          if (row.organization) {
            creatorName = row.organization.displayName;
          } else if (row.customer) {
            creatorName = row.customer.displayName;
          }
        } else if (row.cart?.displayName) {
          creatorName = row.cart.displayName;
        }
        return <div className="d-flex align-items-center">{creatorName}</div>;
      },
      width: "150px",
    },
    {
      name: <h6 className="fw-bold">Source Cart</h6>,
      selector: (row: Order) => row.cart?.displayName || "",
      cell: (row: Order) => {
        const sourceCartName = row.cart?.displayName;

        return (
          <div className="d-flex align-items-center justify-content-between w-100 gap-2">
            <div className="flex-grow-1">
              {sourceCartName ? (
                <span className="text-primary">{sourceCartName}</span>
              ) : (
                <span className="text-warning d-flex align-items-center">
                  Unassigned
                </span>
              )}
            </div>
          </div>
        );
      },
      width: "160px",
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Delivery Cart</h6>,
      selector: (row: Order) => row.deliveryCart?.displayName || "",
      cell: (row: Order) => {
        // Check for deliveryCart object first
        const deliveryCartName = row.deliveryCart?.displayName;

        return (
          <div className="d-flex align-items-center justify-content-between w-100 gap-2">
            <div className="flex-grow-1">
              {deliveryCartName ? (
                // Show delivery cart name if it exists
                <span className="text-success">{deliveryCartName}</span>
              ) : (
                // Show unassigned if neither exists
                <span className="text-warning d-flex align-items-center">
                  Unassigned
                </span>
              )}
            </div>
          </div>
        );
      },
      width: "150px",
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Type</h6>,
      cell: (row: Order) => {
        const orderType = getOrderType(row);
        return (
          <Badge
            color={getTypeBadgeColor(orderType)}
            pill
            className="px-3 py-2"
          >
            {orderType}
          </Badge>
        );
      },
      width: "100px",
      center: true,
    },
    {
      name: <h6 className="fw-bold">Address</h6>,
      selector: (row: Order) => row.address || "",
      sortable: true,
      cell: (row: Order) => {
        const address = row.address || "No address provided";

        const handleCopyAddress = async () => {
          try {
            await navigator.clipboard.writeText(address);
            toast.success("Address copied to clipboard!");
          } catch (err) {
            console.error("Failed to copy address: ", err);
            toast.error("Failed to copy address");
          }
        };

        return (
          <div className="d-flex align-items-center justify-content-between w-100 gap-2">
            <div
              className="flex-grow-1 text-truncate"
              title={address}
              style={{ cursor: "help" }}
            >
              {address}
            </div>

            <div className="flex-shrink-0">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleCopyAddress}
                title="Copy address to clipboard"
              >
                📋
              </button>
            </div>
          </div>
        );
      },
      width: "200px",
    },
    {
      name: <h6 className="fw-bold">Payment Method</h6>,
      selector: (row: Order) => row.paymentMethod || "",
      sortable: true,
      cell: (row: Order) => <div>{row.paymentMethod}</div>,
      width: "120px",
    },
    {
      name: <h6 className="fw-bold">Delivery Date</h6>,
      selector: (row: Order) => formatFirestoreTimestamp(row.deliverAt),
      sortable: true,
      width: "150px",
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      cell: (row: Order) => (
        <Button onClick={() => handleViewDetails(row)} variant="light">
          <FontAwesomeIcon icon={faEye} color="#FF9A62" />
        </Button>
      ),
      width: "90px",
      center: true,
    },
  ];

  const unassignedOrders = orders.filter((order) => !order.displayName);

  const shouldShowDeliveryCartFilter =
    orders.length > 0 &&
    orders.some((order) => Boolean(order?.deliveryCart?.displayName));
  // Filter bulk actions based on current status
  const filteredBulkActions = BULK_ACTIONS.filter((action) => {
    if (action.value === "issue") {
      // Only show issue action for ApprovedByRestaurant status
      return currentStatusForSelected === "ApprovedByRestaurant";
    }
    return true;
  });

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div className="delivery-order-container">
        <div className="header">
          <div className="header-content d-flex justify-content-between align-items-center">
            <h1 className="title">Delivery Orders</h1>
            <button
              className="btn btn-outline-primary"
              onClick={handlePrint}
              disabled={filteredOrders.length === 0}
            >
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              Print
            </button>
          </div>
        </div>

        <Card className="mb-0">
          <CardBody>
            <Row className="align-items-end">
              <Col lg="5" className="mb-2 mt-2">
                <Input
                  type="text"
                  value={searchQuery}
                  placeholder="Search using Order Ref, Phone Number, or Customer/ Organization Name"
                  onChange={handleSearchQueryChange}
                />
              </Col>
            </Row>

            <Row className="align-items-end">
              <Col lg="2" className="mb-3">
                <Label>Order Created By</Label>
                <Select
                  isMulti={false}
                  value={selectedOrderCreatedBy}
                  options={ORDER_CREATED_BY_OPTIONS}
                  onChange={handleOrderCreatedByChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Organization</Label>
                <Select
                  isMulti={false}
                  name="organization"
                  value={selectedOrganization}
                  options={organizationOptions}
                  onChange={handleOrganizationChange}
                  isClearable={true}
                  placeholder="Select organization..."
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Source Cart</Label>
                <Select
                  isMulti={false}
                  value={selectedSourceCart}
                  isSearchable={true}
                  isClearable={true}
                  isLoading={ordersLoading}
                  onInputChange={handleSourceCartInputChange}
                  onChange={handleSourceCartSelect}
                  options={sourceSearchOptions}
                  placeholder={ordersLoading ? "Loading..." : "Select source cart..."}
                />
              </Col>

              {shouldShowDeliveryCartFilter && (
                <Col lg="2" className="mb-3">
                  <Label>Delivery Cart</Label>
                  <Select
                    isMulti={false}
                    value={selectedDeliveryCart}
                    isSearchable={true}
                    isClearable={true}
                    isLoading={ordersLoading}
                    onInputChange={handleDeliveryCartInputChange}
                    onChange={handleDeliveryCartSelect}
                    options={deliverySearchOptions}
                    placeholder={ordersLoading ? "Loading..." : "Select delivery cart..."}
                  />
                </Col>
              )}

              <Col lg="2" className="mb-3">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={deliveryStartDate.split("T")[0]}
                  max={deliveryEndDate.split("T")[0]}
                  onChange={handleStartDateChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={deliveryEndDate.split("T")[0]}
                  min={deliveryStartDate.split("T")[0]}
                  onChange={handleEndDateChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Order Status</Label>
                <Select
                  isMulti={false}
                  value={selectedOrderStatus}
                  options={ORDER_STATUS_OPTIONS}
                  onChange={handleOrderStatusChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Delivery Status</Label>
                <Select
                  isMulti={false}
                  value={selectedDeliveryStatus}
                  options={DELIVERY_STATUS_OPTIONS}
                  onChange={handleDeliveryStatusChange}
                />
              </Col>

              <Col lg="2" className="mb-3">
                <Label>Agent / Source</Label>
                <Select
                  isMulti={false}
                  name="agentSource"
                  value={selectedAgent}
                  options={agentOptions}
                  onChange={handleAgentChange}
                  isClearable={true}
                  placeholder="Select agent or source..."
                />
              </Col>
            </Row>

            <Row className="align-items-end">
              <Col lg="3" className="mb-3">
                <Label>Bulk Actions</Label>
                <Select
                  isMulti={false}
                  value={bulkAction}
                  options={filteredBulkActions}
                  onChange={handleBulkActionChange}
                  placeholder="Select bulk action..."
                  isDisabled={selectedOrders.length === 0}
                />
              </Col>
              <Col lg="8" className="mb-4">
                <div className="d-flex flex-wrap align-items-center gap-3">
                  {unassignedOrders.length > 0 && (
                    <Badge
                      color="warning"
                      className="d-flex align-items-center"
                    >
                      <AlertTriangle size={12} className="me-1" />
                      {unassignedOrders.length} unassigned
                    </Badge>
                  )}

                  <Badge color="success">
                    {selectedOrders.length} selected
                  </Badge>

                  {currentStatusForSelected && (
                    <Badge color="info">
                      Status:{" "}
                      {currentStatusForSelected
                        .replace(/([A-Z])/g, " $1")
                        .trim()}
                    </Badge>
                  )}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            {ordersLoading ? (
              <TableLoadingComponent numColumns={5} />
            ) : (
              <DataTable
                columns={columns}
                data={filteredOrders}
                pagination
                responsive
                highlightOnHover
                paginationPerPage={deliveryRowsPerPage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                progressPending={ordersLoading}
                onRowClicked={handleViewDetails}
                pointerOnHover
              />
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        isOpen={showBulkStatusModal}
        toggle={() => setShowBulkStatusModal(false)}
      >
        <ModalHeader toggle={() => setShowBulkStatusModal(false)}>
          Update Order Status
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>Current Status</Label>
            <Input
              type="text"
              value={currentStatusForSelected.replace(/([A-Z])/g, " $1").trim()}
              readOnly
            />
          </div>
          <div className="mb-3">
            <Label>Next Status</Label>
            <Input type="text" value={nextStatusOption?.label || ""} readOnly />
          </div>
          <div className="mb-3">
            <Label>Number of Orders</Label>
            <Input type="text" value={selectedOrders.length} readOnly />
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonComponent
            onClick={() => setShowBulkStatusModal(false)}
            titel="Cancel"
          />
          <ButtonComponent
            onClick={handleBulkActionSubmit}
            isDisabled={isUpdatingBulkStatus}
            titel={isUpdatingBulkStatus ? "Processing..." : "Update"}
          />
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(false)}
      >
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          Confirm Order Issuance
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>Number of Orders to Issue</Label>
            <Input type="text" value={selectedOrders.length} readOnly />
          </div>
          <div className="alert alert-warning">
            Are you sure you want to issue these {selectedOrders.length} orders?
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonComponent
            onClick={() => setShowConfirmModal(false)}
            titel="Cancel"
          />
          <ButtonComponent
            onClick={handleIssueOrders}
            isDisabled={isIssuingBulkOrder}
            titel={isIssuingBulkOrder ? "Processing..." : "Yes, Issue Orders"}
          />
        </ModalFooter>
      </Modal>

      <DeliveryPrintSetupModal
        showModal={showPrintModal}
        handleModalClose={() => setShowPrintModal(false)}
        orderList={filteredOrders}
        cartName={selectedSourceCart?.displayName || "All"}
        orderDate={
          deliveryStartDate && deliveryEndDate
            ? `${dateTFormat(new Date(deliveryStartDate))} - ${dateTFormat(new Date(deliveryEndDate))}`
            : deliveryStartDate
              ? dateTFormat(new Date(deliveryStartDate))
              : ""
        }
        isAllCartsSelected={selectedSourceCart?.cartId === "all"}
        isSummaryView={false}
      />
    </>
  );
};

export default DeliveryOrderManagement;